import { queueVariantOf, type Snapshot } from "@app/protocol";
import { useT } from "../i18n/context";
import { send } from "../ws/singleton";

const pill = (tone: "glass" | "green" | "gold", size: string) =>
  tone === "glass"
    ? `btn-panel flex items-center whitespace-nowrap rounded-pill font-bold ${size}`
    : `btn-${tone} flex items-center whitespace-nowrap rounded-pill font-extrabold ${size}`;

/** A channel's table looks for players for its empty seats, or stops (decision 126 A). */
function QueueButton({ open, size }: { open: boolean; size: string }) {
  const { t } = useT();
  return (
    <button
      type="button"
      className={pill(open ? "glass" : "green", size)}
      onClick={() => send({ type: "queue", open: !open })}
    >
      {t(open ? "match.stop" : "match.find")}
    </button>
  );
}

/** Side by side on phone landscape, stacked on the narrow portrait table, wrapping on wide. */
function buttonsLayout(dense: boolean, row: boolean): string {
  if (row) return "gap-1.5";
  return dense ? "flex-col gap-1.5" : "flex-wrap gap-2.5";
}

/** "Nobody new for a minute", read out once it appears (decision 124). */
function StalledNote({ dense }: { dense: boolean }) {
  const { t } = useT();
  return (
    <span
      role="status"
      className={`text-legible font-bold text-ink/90 ${dense ? "whitespace-nowrap text-[11px]" : "text-[12px]"}`}
    >
      {t(dense ? "match.stalled.short" : "match.stalled")}
    </span>
  );
}

/**
 * The owner's buttons in the lobby: look for players, fill the empty seats with bots, or start
 * early during the countdown. After a minute with nobody new, filling asks for attention (124).
 */
export function OwnerButtons({
  table,
  emptySeats,
  stalled,
  size,
  dense,
  row,
}: {
  table: Snapshot["table"];
  emptySeats: number;
  stalled: boolean;
  size: string;
  /** Phones: one short line. */
  dense: boolean;
  /** Buttons side by side (phone landscape) rather than stacked (the narrow portrait table). */
  row: boolean;
}) {
  const { t } = useT();
  if (table.status === "countdown")
    return (
      <button type="button" className={pill("gold", size)} onClick={() => send({ type: "start" })}>
        {t("lobby.start")}
      </button>
    );
  if (table.status !== "lobby" || emptySeats === 0) return null;
  // A table the queue formed is always open to it; a channel's table chooses.
  const channel = queueVariantOf(table.id) === null;
  return (
    <div className={`flex flex-col items-center ${dense ? "gap-1" : "gap-2.5"}`}>
      {stalled ? <StalledNote dense={dense} /> : null}
      <div className={`flex items-center justify-center ${buttonsLayout(dense, row)}`}>
        {channel ? <QueueButton open={table.queue} size={size} /> : null}
        <button
          type="button"
          className={pill(stalled ? "gold" : "glass", size)}
          onClick={() => send({ type: "fillBots" })}
        >
          {t("lobby.fillBots")}
        </button>
      </div>
    </div>
  );
}
